import { useParams, Navigate, Link } from "react-router-dom";
import { ArrowLeft, ArrowRight } from "lucide-react";
import Seo from "@/components/Seo";
import { Reveal } from "@/components/Reveal";
import { Section } from "@/components/Bits";
import ImagePlaceholder from "@/components/ImagePlaceholder";
import { ARTICLES } from "@/data/site";

export default function Article() {
  const { slug } = useParams();
  const idx = ARTICLES.findIndex((a) => a.slug === slug);
  if (idx === -1) return <Navigate to="/guides" replace />;
  const article = ARTICLES[idx];
  const next = ARTICLES[(idx + 1) % ARTICLES.length];

  return (
    <>
      <Seo title={`${article.title} | FreeRange.Education`} description={article.excerpt} />

      <Section className="pb-10 pt-32 md:pt-40">
        <Reveal>
          <Link to="/guides" className="mb-8 inline-flex items-center gap-2 text-sm font-semibold text-charcoal/60 hover:text-charcoal" data-testid="back-to-guides">
            <ArrowLeft className="h-4 w-4" /> Ideas & Guides
          </Link>
          <div className="max-w-3xl">
            <span className="text-xs font-bold uppercase tracking-[0.18em] text-marigold">{article.category} · {article.readTime}</span>
            <h1 className="mt-4 font-display text-4xl font-extrabold leading-[1.02] tracking-tight text-charcoal sm:text-5xl lg:text-6xl">
              {article.title}
            </h1>
            <p className="mt-5 text-xl leading-relaxed text-charcoal/70">{article.excerpt}</p>
          </div>
        </Reveal>
      </Section>

      <Section className="pb-12">
        <Reveal>
          <div className="group overflow-hidden rounded-[2rem]">
            <ImagePlaceholder src={article.image} alt={article.title} ratio="wide" rounded="rounded-[2rem]" label="ARTICLE HERO IMAGE PLACEHOLDER" testid="article-hero-img" />
          </div>
        </Reveal>
      </Section>

      <Section className="pb-24 md:pb-32">
        <Reveal className="mx-auto max-w-2xl">
          <div className="space-y-6 text-lg leading-relaxed text-charcoal/85">
            {article.body.map((p, i) => (
              <p
                key={i}
                className={i === 0 ? "first-letter:float-left first-letter:mr-3 first-letter:font-display first-letter:text-7xl first-letter:font-extrabold first-letter:leading-[0.8] first-letter:text-purple" : ""}
              >
                {p}
              </p>
            ))}
          </div>

          <div className="my-12 border-l-4 border-turquoise pl-6">
            <p className="font-display text-2xl font-bold leading-snug tracking-tight text-purple">
              Tools built for home education — not school software adapted for home.
            </p>
          </div>

          <p className="text-sm italic text-charcoal/50">
            This is example content created to demonstrate the article layout. Fuller guides will be published over time.
          </p>
        </Reveal>
      </Section>

      <Section className="pb-28">
        <Reveal>
          <Link
            to={`/guides/${next.slug}`}
            className="group flex flex-col gap-3 rounded-[2rem] bg-sand p-8 transition-colors duration-300 hover:bg-sunny/40 md:flex-row md:items-center md:justify-between md:p-12"
            data-testid="next-article"
          >
            <div>
              <span className="text-xs font-bold uppercase tracking-[0.18em] text-marigold">Read next</span>
              <h3 className="mt-2 font-display text-2xl font-bold tracking-tight text-charcoal sm:text-3xl">{next.title}</h3>
            </div>
            <ArrowRight className="h-8 w-8 text-purple transition-transform duration-300 group-hover:translate-x-2" />
          </Link>
        </Reveal>
      </Section>
    </>
  );
}
